import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import type { AuthUser } from '../auth/types'
import { confirmSallaInvitation, previewSallaInvitation, type SallaInvitationPreview } from '../channels/sallaInvitationsApi'
import { clearSallaInvitation } from '../channels/sallaInvitationSession'
import { TlaaqLogo } from '../components/brand/TlaaqLogo'
import { LocaleSwitcher } from '../components/locale/LocaleSwitcher'
import { Button, Card, ChannelDot, TEXT_WRAP_STYLE, MOBILE_FULL_WIDTH_BUTTON_STYLE } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { SallaOAuthPage } from './SallaOAuthPage'

interface Props {
  token: string | null
  session: { accessToken: string; user: AuthUser } | null
  onLogin: () => void
  onRegister: () => void
  onDone: () => void
}

function failureState(error: unknown): 'invalid' | 'nonowner' | 'conflict' | 'unavailable' {
  if (error instanceof ApiClientError) {
    if (error.code === 'SALLA_INVITATION_INVALID') return 'invalid'
    if (error.code === 'OWNER_REQUIRED') return 'nonowner'
    if (error.code === 'CHANNEL_EXTERNAL_ACCOUNT_CONFLICT') return 'conflict'
  }
  return 'unavailable'
}

export function SallaInvitationPage({ token, session, onLogin, onRegister, onDone }: Props) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [preview, setPreview] = useState<SallaInvitationPreview | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [attempt, setAttempt] = useState(0)
  const [busy, setBusy] = useState(false)
  const confirming = useRef(false)
  const [connectionId, setConnectionId] = useState<number | null>(null)
  const accessToken = session?.accessToken
  const owner = session?.user.role === 'OWNER'

  useEffect(() => {
    if (!token || !accessToken || !owner) return
    let active = true
    previewSallaInvitation(accessToken, token).then(value => {
      if (active) { setPreview(value); setError(null) }
    }).catch(caught => { if (active) setError(caught) })
    return () => { active = false }
  }, [accessToken, token, owner, attempt])

  async function confirm() {
    if (!session || !token || !preview || confirming.current) return
    confirming.current = true
    setBusy(true)
    try {
      const result = await confirmSallaInvitation(session.accessToken, token, preview.organizationId)
      clearSallaInvitation()
      setConnectionId(result.connectionId)
    } catch (caught) {
      setError(caught)
    } finally {
      confirming.current = false
      setBusy(false)
    }
  }

  if (connectionId && session) {
    return <SallaOAuthPage accessToken={session.accessToken} connectedConnectionId={connectionId} onDone={onDone} />
  }

  const state = !token ? 'invalid' : session && !owner ? 'nonowner' : error ? failureState(error)
    : !session ? 'continue' : !preview ? 'loading' : 'confirm'
  const recovery = ['invalid', 'nonowner', 'conflict', 'unavailable'].includes(state)
  return <main style={{ minHeight: '100dvh', boxSizing: 'border-box', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 'var(--sp-6)' : 'var(--sp-12)', padding: isMobile ? 'var(--sp-4)' : 'var(--sp-12)' }}>
    <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 'var(--sp-4)' }}>
      <TlaaqLogo height={30} />
      <LocaleSwitcher />
    </header>
    <Card style={{ width: 480, maxWidth: '100%', boxSizing: 'border-box', display: 'grid', gap: 'var(--sp-6)', padding: isMobile ? 'var(--sp-6)' : 'var(--sp-8)', ...TEXT_WRAP_STYLE }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', color: 'var(--fg-muted)', fontSize: 'var(--fs-14)' }}>
        <ChannelDot name="Salla" size={28} />{t('pages.sallaInvitation.eyebrow')}
      </div>
      <div style={{ display: 'grid', gap: 'var(--sp-3)' }} aria-live="polite">
        <h1 style={{ margin: 0, fontSize: 'var(--fs-30)', fontWeight: 'var(--fw-bold)', lineHeight: 'var(--lh-snug)' }}>{t(`pages.sallaInvitation.${state}.title`)}</h1>
        <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 'var(--fs-16)', lineHeight: 'var(--lh-relaxed)' }}>{t(`pages.sallaInvitation.${state}.body`)}</p>
      </div>
      {state === 'confirm' && preview && session && <dl style={{ margin: 0, display: 'grid', gap: 'var(--sp-4)', padding: 'var(--sp-5)', background: 'var(--bg)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)' }}>
        <dt style={{ color: 'var(--fg-muted)', fontSize: 'var(--fs-14)' }}>{t('pages.sallaInvitation.store')}</dt>
        <dd style={{ margin: 0, fontSize: 'var(--fs-20)', fontWeight: 'var(--fw-semibold)' }}>{preview.storeName}</dd>
        <div role="separator" style={{ height: 1, background: 'var(--border-subtle)' }} />
        <dt style={{ color: 'var(--fg-muted)', fontSize: 'var(--fs-14)' }}>{t('pages.sallaInvitation.organization')}</dt>
        <dd style={{ margin: 0, fontSize: 'var(--fs-20)', fontWeight: 'var(--fw-semibold)' }}>{session.user.organizationName}</dd>
      </dl>}
      <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
        {state === 'continue' && <>
          <Button size="lg" style={MOBILE_FULL_WIDTH_BUTTON_STYLE} onClick={onRegister}>{t('pages.sallaInvitation.register')}</Button>
          <Button kind="secondary" size="lg" style={MOBILE_FULL_WIDTH_BUTTON_STYLE} onClick={onLogin}>{t('pages.sallaInvitation.login')}</Button>
        </>}
        {state === 'confirm' && <Button size="lg" style={MOBILE_FULL_WIDTH_BUTTON_STYLE} onClick={() => { void confirm() }} disabled={busy}>{t(busy ? 'pages.sallaInvitation.confirming' : 'pages.sallaInvitation.confirmAction')}</Button>}
        {state === 'nonowner' && <Button size="lg" style={MOBILE_FULL_WIDTH_BUTTON_STYLE} onClick={onLogin}>{t('pages.sallaInvitation.switchAccount')}</Button>}
        {state === 'unavailable' && <Button size="lg" style={MOBILE_FULL_WIDTH_BUTTON_STYLE} onClick={() => { setError(null); setPreview(null); setAttempt(value => value + 1) }}>{t('pages.sallaInvitation.retry')}</Button>}
        {recovery && <a href="mailto:support@tlaaq.com" style={{ color: 'var(--brand-700)', fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-semibold)', textAlign: 'center', padding: 'var(--sp-3)' }}>{t('pages.sallaInvitation.support')}</a>}
        {state !== 'continue' && <Button kind="secondary" size="lg" style={MOBILE_FULL_WIDTH_BUTTON_STYLE} onClick={onDone} disabled={busy}>{t(state === 'confirm' ? 'pages.sallaInvitation.cancel' : 'pages.sallaInvitation.back')}</Button>}
      </div>
      <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 'var(--fs-14)', lineHeight: 'var(--lh-relaxed)' }}>{t(`pages.sallaInvitation.${state === 'confirm' ? 'importHint' : state === 'continue' ? 'authHint' : 'privacyHint'}`)}</p>
    </Card>
  </main>
}
